'use client'

import * as React from 'react'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { motion } from 'framer-motion'

import { buttonVariants } from '@/components/ui/button'
import { IconMessage, IconUsers } from '@/components/ui/icons'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { useLocalStorage } from '@/lib/hooks/use-local-storage'
import { type Chat } from '@/lib/types'
import { cn } from '@/lib/utils'

import { getAgentConfig } from '@/lib/config/agents'

import "@/app/globals.css"

interface SidebarItemProps {
  index: number
  chat: Chat & { agent?: string }
  children: React.ReactNode
}

export function SidebarItem({ index, chat, children }: SidebarItemProps) {
  // Defensive: skip rendering if chat or chat.id is missing
  if (!chat?.id) return null;
  const pathname = usePathname();
  const isActive = pathname.includes(chat.path || '');
  const [newChatId, setNewChatId] = useLocalStorage('newChatId', null);
  const shouldAnimate = index === 0 && isActive && newChatId;
  return (
    <motion.div className="relative h-8" variants={{
        initial: {
          height: 0,
          opacity: 0
        },
        animate: {
          height: 'auto',
          opacity: 1
        }
      }}
      initial={shouldAnimate ? 'initial' : undefined}
      animate={shouldAnimate ? 'animate' : undefined}
      transition={{
        duration: 0.25,
        ease: 'easeIn'
      }}
    >
      <div className="absolute left-2 top-1 flex size-6 items-center justify-center">
        {chat.sharePath ? (
          <Tooltip delayDuration={1000}>
            <TooltipTrigger tabIndex={-1} className="focus:bg-muted focus:ring-1 focus:ring-ring">
              <IconUsers className="mr-2 mt-1 text-aw-text-muted" />
            </TooltipTrigger>
            <TooltipContent>This is a shared chat.</TooltipContent>
          </Tooltip>
        ) : (
          <IconMessage className="mr-2 mt-1 text-aw-text-muted" />
        )}
      </div>
      <Link
        href={chat.path || '#'}
        className={cn(
          buttonVariants({ variant: 'ghost' }),
          'group w-full px-8 transition-colors hover:bg-zinc-500/50',
          isActive && 'bg-aw-secondary pr-16 font-semibold text-aw-bg-page',
        )}
      >
        <div className="relative max-h-5 flex-1 select-none overflow-hidden text-ellipsis break-all" title={chat.title || 'Untitled'}>
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap flex-1 min-w-0 overflow-hidden text-ellipsis">
              {shouldAnimate && chat.title ? (
                chat.title.split('').map((character, idx) => (
                  <motion.span
                    key={idx}
                    variants={{ initial: { opacity: 0, x: -100 }, animate: { opacity: 1, x: 0 } }}
                    initial={shouldAnimate ? 'initial' : undefined}
                    animate={shouldAnimate ? 'animate' : undefined}
                    transition={{ duration: 0.25, ease: 'easeIn', delay: idx * 0.05, staggerChildren: 0.05 }}
                    onAnimationComplete={() => { if (idx === chat.title.length - 1) setNewChatId(null); }}
                  >
                    {character}
                  </motion.span>
                ))
              ) : (
                <span>{chat.title || 'Untitled'}</span>
              )}
            </span>
            {chat.agent && (
              <span 
                className={cn(
                  'px-1.5 py-0.5 text-xs font-medium text-white rounded-md shrink-0',
                  getAgentConfig(chat.agent).color
                )}
                title={`${getAgentConfig(chat.agent).displayName} Agent`}
              >
                {getAgentConfig(chat.agent).displayName}
              </span>
            )}
          </div>
        </div>
      </Link>
      {isActive && <div className="absolute right-2 top-1">{children}</div>}
    </motion.div>
  );
}
